'use client';

import { Stack, Typography } from '@mui/material';
import Image from 'next/image';
import { useState } from 'react';

import { icons } from '@/assets/icons';
import HexagonalButton from '@/components/bases/HexagonalButton';
import { useGA } from '@/hooks/useGA';

export const StayConnectedSection = () => {
  const { trackButtonClickEvent } = useGA();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');
    setMessage('');

    // Mailchimp JSONP URL
    const url = process.env.NEXT_PUBLIC_MAILCHIMP_URL || '';
    const callbackName = 'jsonp_callback_' + Math.round(10000 * Math.random());
    const fullUrl = `${url}&c=${callbackName}&EMAIL=${encodeURIComponent(email)}`;

    const script = document.createElement('script');
    script.src = fullUrl;

    // Define the global callback function
    (window as any)[callbackName] = (data: any) => {
      // Clean up
      delete (window as any)[callbackName];
      document.body.removeChild(script);

      if (data.result === 'success') {
        setStatus('success');
        setMessage('Thank you for subscribing!');
        setEmail('');
        setTimeout(() => {
          setMessage('');
          setStatus('idle');
        }, 5000);
      } else {
        setStatus('error');
        // Strip HTML from error message if present, or use a default
        let msg = data.msg || 'Something went wrong. Please try again.';
        if (msg.includes('0 - ')) {
          msg = msg.replace('0 - ', '');
        }
        setMessage(msg);
      }
    };

    document.body.appendChild(script);
    trackButtonClickEvent({ name: 'newsletter_subscribe' });
  };

  return (
    <Stack
      width={'100%'}
      height={'100%'}
      flexGrow={1}
      padding={{ xs: '40px 24px', sm: '40px 80px' }}
      alignItems={'center'}
      component={'section'}
      position={'relative'}>
      <Stack width={'min(1280px, 100%)'} zIndex={2} gap={{ xs: 5, sm: 8 }}>
        <Stack
          alignItems={'center'}
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent={'space-between'}
          gap={{ xs: '40px', sm: '80px' }}
          width={'100%'}>
          <Image
            src={icons.ic_graphic_mail}
            alt={'newsletter'}
            width={413}
            height={357}
            className={'w-[207px] sm:w-[413px] h-[179px] sm:h-[357px]'}
          />
          <Stack gap={{ xs: 3, sm: 7 }} width={'100%'}>
            <Stack gap={{ xs: 2, sm: 3 }} width={'100%'}>
              <Typography
                component={'h2'}
                variant={'displayMedium'}
                className={'clip-text'}
                fontSize={{ xs: '24px', sm: '48px' }}
                lineHeight={{ xs: '32px', sm: '68px' }}
                textAlign={{ xs: 'center', sm: 'left' }}
                sx={{ background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)' }}>
                STAY <span style={{ WebkitTextFillColor: '#FF6B00' }}>CONNECTED</span>
              </Typography>
              <Typography
                fontSize={{ xs: '14px', sm: '18px' }}
                component={'h4'}
                variant={'bodyLarge'}
                color={'#FFFFFF'}
                className={'mix-blend'}
                textAlign={{ xs: 'center', sm: 'left' }}>
                We move fast with the community in mind. Join the LFG movement.
              </Typography>
            </Stack>

            {/* Newsletter Form */}
            <Stack width={'100%'}>
              <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} width={'100%'} alignItems={'flex-start'}>
                  <Stack width={'100%'} sx={{ flex: 1 }}>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="example@email.com"
                      aria-label="Email Address"
                      style={{
                        width: '100%',
                        height: '52px',
                        padding: '16px',
                        background: 'rgba(255, 255, 255, 0.02)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontFamily: '"Gotham", sans-serif',
                        fontSize: '16px',
                        boxSizing: 'border-box',
                        outline: 'none',
                      }}
                      className="focus:border-[rgba(255,255,255,0.3)] focus:bg-[rgba(255,255,255,0.04)] transition-all duration-200"
                    />
                  </Stack>
                  <div style={{ width: '100%', maxWidth: '180px' }}>
                    <HexagonalButton
                      label={status === 'loading' ? 'SUBSCRIBING...' : 'SUBSCRIBE'}
                      variant="primary"
                      type="submit"
                      width={'100%'}
                      disabled={status === 'loading'}
                    />
                  </div>
                </Stack>
              </form>

              {/* Status Messages */}
              {message && (
                <Stack
                  mt={1}
                  padding="8px 16px"
                  borderRadius="4px"
                  bgcolor={status === 'success' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 83, 25, 0.1)'}
                  border={`1px solid ${status === 'success' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 83, 25, 0.3)'}`}
                >
                  <Typography
                    fontSize="14px"
                    color={status === 'success' ? '#4CAF50' : '#FF5319'}
                    sx={{ fontFamily: '"Gotham", sans-serif' }}
                    dangerouslySetInnerHTML={{ __html: message }}
                  />
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
